import { api } from './api';
import {
  ImpersonationMode,
  StartImpersonationResponse,
  EndImpersonationResponse,
  RestoreIdentityResponse,
  CurrentSessionResponse,
  RequestAuditResponse,
} from '../types/Impersonation';
import { showToast } from '../utils/toast';

export const impersonationService = {
  startImpersonation: (
    userId: number,
    mode?: ImpersonationMode,
  ): Promise<StartImpersonationResponse> => {
    const body: { mode?: ImpersonationMode } = {};
    if (mode) {
      body.mode = mode;
    }
    return api.post<StartImpersonationResponse>(
      `/users/${userId}:impersonate`,
      body,
    );
  },

  endImpersonation: (sessionId: string): Promise<EndImpersonationResponse> => {
    return api.post<EndImpersonationResponse>(
      `/impersonation-sessions/${sessionId}:end`,
      {},
    );
  },

  restoreIdentity: (): Promise<RestoreIdentityResponse> => {
    return api.post<RestoreIdentityResponse>('/auth/restore-identity', {});
  },

  getCurrentSession: (): Promise<CurrentSessionResponse> => {
    return api.get<CurrentSessionResponse>('/impersonation-sessions/current');
  },

  getImpersonationAudit: (requestId: number): Promise<RequestAuditResponse> => {
    return api.get<RequestAuditResponse>(
      `/requests/${requestId}/impersonation-audit`,
    );
  },
};

/**
 * Check if an error is an IMPERSONATION_READ_ONLY_VIOLATION 403 response.
 * Returns true if the error matches, false otherwise.
 *
 * ⚠️ THE CHECK IS STRUCTURAL, NOT `instanceof ApiError` (#1069 Story 6.5 code
 * review, 2026-09-17, closing C17). The refusal is a property of the RESPONSE
 * (403 carrying the middleware's sentence), not of the class the transport
 * happens to throw, and this product has two transports that can carry it:
 * `api.ts` throws `ApiError`, and `postWithProgress.ts` throws `UploadError`
 * for anything sent with an upload progress channel
 * (`src/services/correspondence/postWithProgress.ts:34-50`, same `status` and
 * `message` fields, no relation in the type hierarchy).
 *
 * With the old `instanceof` gate a read-only impersonation refusing a
 * correspondence post WITH FILES matched nothing: no toast, and the panel
 * printed the raw `IMPERSONATION_READ_ONLY_VIOLATION` text instead of the one
 * sentence `READ_ONLY_VIOLATION_MESSAGE` exists to guarantee. The message test
 * already matched -- `impersonationReadOnly.ts:103-107` sends
 * `{ error: 'Data mutations are not allowed in Read Only impersonation mode' }`
 * and `postWithProgress.ts:107-110` puts exactly that string on the error --
 * so the nominal type check was the only thing failing, standing in for a
 * property it does not describe.
 */
export const isReadOnlyViolationError = (error: unknown): boolean => {
  const candidate = error as { status?: unknown; message?: unknown } | null;
  if (candidate?.status !== 403) return false;
  const message = candidate.message;
  if (typeof message !== 'string') return false;
  return (
    message.includes('Read Only impersonation mode') ||
    message.includes('IMPERSONATION_READ_ONLY_VIOLATION')
  );
};

/**
 * The ONE sentence the product says when a read-only impersonation refuses a
 * write.
 *
 * ⚠️ EXPORTED SO A SECOND CHANNEL CANNOT DISAGREE WITH THE TOAST (#1069 Story
 * 6.5, D7). The correspondence panel shows its own line under the composer
 * ("did my message go") beside this toast ("why is the app refusing me"), and a
 * feature that re-typed the sentence would eventually drift from it. Anything
 * that needs this wording reads it here; nobody writes it twice.
 */
export const READ_ONLY_VIOLATION_MESSAGE =
  'This action is not allowed in Read Only mode.';

/**
 * Handle IMPERSONATION_READ_ONLY_VIOLATION errors by showing a user-friendly
 * toast message. Returns true if the error was handled, false otherwise.
 * Callers should check the return value to decide whether to show their own error.
 */
export const handleReadOnlyViolationError = (error: unknown): boolean => {
  if (isReadOnlyViolationError(error)) {
    showToast.error(READ_ONLY_VIOLATION_MESSAGE);
    return true;
  }
  return false;
};
